import React, { useCallback, useMemo, useState } from 'react';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import * as XLSX from 'xlsx';
import { getAllAttributes, getAttributeOptions, deleteAttribute } from '../../services/productServices';
import AddAttributeDialog from './AddAttributeDialog';
import ConfirmDialog from '../ui/ConfirmDialog';
import NotificationSnackbar from '../ui/NotificationSnackbar';
import { CrmButton, CrmTooltip } from '../ui';
import { CrmEntityListPage } from '../listing';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const exportAttributes = (rows, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(rows.map((attribute) => ({
    Attribute: attribute.name || '',
    Options: Array.isArray(attribute.options) ? attribute.options.map((option) => option.value).join(', ') : '',
    'Options count': Number(attribute.optionsCount || 0),
  })));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Attributes');
  XLSX.writeFile(workbook, filename);
};

function AttributeList() {
  const [attributes, setAttributes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = useCallback((message, severity = 'info') => setSnackbar({ open: true, message, severity }), []);

  const fetchAttributes = useCallback(async ({ isAutoRefresh = false } = {}) => {
    if (!isAutoRefresh) setLoading(true);
    try {
      const raw = await getAllAttributes();
      const enriched = await Promise.all((Array.isArray(raw) ? raw : []).map(async (attribute) => {
        const options = await getAttributeOptions(attribute.id);
        return { ...attribute, options: Array.isArray(options) ? options : [], optionsCount: Array.isArray(options) ? options.length : 0 };
      }));
      setAttributes(enriched);
    } catch (error) {
      console.error(error);
      showSnackbar('Failed to load attributes', 'error');
    } finally {
      if (!isAutoRefresh) setLoading(false);
    }
  }, [showSnackbar]);

  useAutoRefresh(fetchAttributes, { intervalMs: 20000 });

  const columns = useMemo(() => [
    {
      id: 'name', label: 'Attribute', width: 260, minWidth: 100, maxWidth: 520, hideable: false,
      renderCell: (row) => <button type="button" className="crm-table-primary-link" onClick={() => { setEditingAttribute(row); setOpenDialog(true); }}>{row.name || '—'}</button>,
    },
    {
      id: 'options', label: 'Options', width: 420, minWidth: 100, maxWidth: 720,
      accessor: (row) => (row.options || []).map((option) => option.value).join(', '),
      renderCell: (row) => (row.options || []).map((option) => option.value).join(', ') || '—',
    },
    { id: 'optionsCount', label: 'Options Count', width: 140, minWidth: 100, maxWidth: 220, type: 'number', accessor: (row) => Number(row.optionsCount || 0) },
    {
      id: 'row_actions', label: 'Actions', width: 110, minWidth: 100, maxWidth: 140, hideable: false, sortable: false, searchable: false,
      renderCell: (row) => <span className="crm-row-actions">
        <CrmTooltip content="Edit attribute"><CrmButton iconOnly size="small" variant="tertiary" onClick={() => { setEditingAttribute(row); setOpenDialog(true); }}><EditOutlinedIcon /></CrmButton></CrmTooltip>
        <CrmTooltip content="Delete attribute"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action--danger" onClick={() => { setDeleteTarget([row.id]); setConfirmOpen(true); }}><DeleteOutlineIcon /></CrmButton></CrmTooltip>
      </span>,
    },
  ], []);

  const filters = useMemo(() => [
    { id: 'optionsCount', columnId: 'optionsCount', label: 'Options count', type: 'number', quick: true },
  ], []);

  const confirmDelete = async () => {
    try {
      for (const id of deleteTarget) await deleteAttribute(id);
      await fetchAttributes({ isAutoRefresh: true });
      showSnackbar(`${deleteTarget.length} attribute${deleteTarget.length === 1 ? '' : 's'} deleted`, 'success');
    } catch (error) {
      showSnackbar(error?.response?.data?.error || 'Failed to delete attribute', 'error');
    } finally {
      setConfirmOpen(false);
      setDeleteTarget([]);
    }
  };

  return <>
    <CrmEntityListPage
      moduleKey="attributes"
      title="Attributes"
      itemLabel="attribute"
      items={attributes}
      columns={columns}
      filterDefinitions={filters}
      initialSort={{ columnId: 'name', direction: 'asc' }}
      loading={loading}
      onRefresh={() => fetchAttributes()}
      onCreate={() => { setEditingAttribute(null); setOpenDialog(true); }}
      createLabel="Add attribute"
      onRowClick={(row) => { setEditingAttribute(row); setOpenDialog(true); }}
      onExport={(rows) => exportAttributes(rows, 'attributes.xlsx')}
      onExportSelected={(rows) => exportAttributes(rows, 'selected_attributes.xlsx')}
      onDeleteSelected={(ids) => { setDeleteTarget(ids); setConfirmOpen(true); }}
    />
    <AddAttributeDialog open={openDialog} attribute={editingAttribute} onClose={() => { setOpenDialog(false); setEditingAttribute(null); fetchAttributes({ isAutoRefresh: true }); }} />
    <ConfirmDialog open={confirmOpen} title="Delete attribute?" message={`Delete ${deleteTarget.length} attribute${deleteTarget.length === 1 ? '' : 's'}?`} confirmText="Delete" onCancel={() => setConfirmOpen(false)} onConfirm={confirmDelete} />
    <NotificationSnackbar {...snackbar} onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))} />
  </>;
}

export default AttributeList;
